import type { Permission } from "@erp/shared";
import { createFileRoute, Link, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { AppShell } from "~/components/layout/app-shell";
import { NAV } from "~/components/layout/nav";
import { useNewVersionOnCrash } from "~/components/layout/system-banners";
import { buttonVariants } from "~/components/ui/button";
import { ErrorState } from "~/components/ui/states";
import { ApiError } from "~/lib/api";
import { useT } from "~/lib/i18n";
import { meQuery, useCan } from "~/lib/session";

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        throw redirect({ to: "/login", search: { redirect: location.href } });
      }
      throw error;
    }
  },
  component: () => (
    <AppShell>
      <SectionGate />
    </AppShell>
  ),
  // Session check failed for a reason other than 401 (API down, offline): offer a retry instead of a blank page.
  errorComponent: AppCrash,
});

function AppCrash({ error, reset }: { error: unknown; reset: () => void }) {
  useNewVersionOnCrash();
  return (
    <div className="grid min-h-dvh place-items-center">
      <ErrorState error={error} onRetry={reset} />
    </div>
  );
}

/** Sections a role may lack: the most specific prefix wins. The API refuses anyway; this avoids half-drawn pages. */
const SECTION_PERMISSIONS: [string, Permission][] = (
  [
    ["/settings/integrations", "settings:manage"],
    ["/settings/users", "users:manage"],
    ["/settings/imports", "imports:run"],
    ["/settings/health", "settings:manage"],
    ["/settings/activity", "audit:read"],
    ["/settings/sales", "sales:read"],
    ["/settings/stock", "stock:read"],
    ["/settings/shipping", "stock:read"],
    ["/settings/sav", "sav:read"],
    ["/stock/valuation", "costs:read"],
    ...NAV.flatMap((section) => section.items).flatMap((item): [string, Permission][] =>
      item.to !== "/" && item.permission ? [[item.to, item.permission]] : [],
    ),
  ] satisfies [string, Permission][]
).toSorted((a, b) => b[0].length - a[0].length);

function SectionGate() {
  const t = useT();
  const can = useCan();
  const { pathname } = useLocation();
  const required = SECTION_PERMISSIONS.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.[1];
  if (!required || can(required)) return <Outlet />;
  return (
    <div className="grid min-h-[60vh] place-items-center px-6">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-medium">{t("errors.pages.forbiddenTitle")}</h1>
        <p className="mt-2 text-fg-2">{t("errors.pages.forbiddenBody")}</p>
        <Link to="/" className={buttonVariants({ variant: "secondary", className: "mt-6" })}>
          {t("errors.pages.home")}
        </Link>
      </div>
    </div>
  );
}
